import { OLButton } from "OLButton";
import { useEvents } from "api/events";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LoadingInterceptor } from "components/LoadingInterceptor/LoadingInterceptor";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  selectImportedCustomEventSchemas,
  setImportedCustomEventSchemas,
} from "reduxState/customEventSchemas/customEventSchemas";
import { ImportedCustomEventSchema } from "types/customEvents";
import { links } from "utilities/links";
import { useStyles } from "./CustomEventSchemaImportList.styles";
import { SuccessfulImports } from "./SuccessfullImports";
import { UnsuccessfulImports } from "./UnsuccessfullImports";

export type UnsuccessfulCustomEventSchema = {
  data: ImportedCustomEventSchema;
  importedId: number;
  code?: number;
  message?: string;
  errors?: any[];
};

type CustomEventSchemasData = {
  saved: ImportedCustomEventSchema[];
  unsaved: UnsuccessfulCustomEventSchema[];
};

export const CustomEventSchemaImportList = () => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { addCustomEventSchemaBatch } = useEvents();
  const importedCustomEventSchemas = useAppSelector(
    selectImportedCustomEventSchemas,
  );
  const formattedImportedCustomEventSchemas = importedCustomEventSchemas.map(
    (customEventSchema) => ({
      ...customEventSchema,
      active: false,
    }),
  );

  const [customEventSchemasData, setCustomEventSchemasData] =
    useState<CustomEventSchemasData>({
      saved: [],
      unsaved: [],
    });
  const [isLoading, setIsLoading] = useState(true);

  const areImportedCustomEventSchemas = importedCustomEventSchemas.length > 0;
  const areSuccessImports = customEventSchemasData.saved.length > 0;
  const areUnsuccessImports = customEventSchemasData.unsaved.length > 0;

  useEffect(() => {
    if (areImportedCustomEventSchemas) {
      addCustomEventSchemaBatch(formattedImportedCustomEventSchemas).then(
        (resp) => {
          dispatch(
            setImportedCustomEventSchemas(
              resp?.map((customEventSchema) => customEventSchema.data),
            ),
          );

          const sortedCustomEventSchemas: CustomEventSchemasData = {
            saved: [],
            unsaved: [],
          };

          resp?.forEach((customEventSchema, index) => {
            if (customEventSchema.data.isSaved) {
              return sortedCustomEventSchemas.saved.push(
                customEventSchema.data,
              );
            }

            sortedCustomEventSchemas.unsaved.push({
              ...customEventSchema,
              importedId: index,
            });
          });

          setCustomEventSchemasData(sortedCustomEventSchemas);
          setIsLoading(false);
        },
      );
    } else {
      navigate(links.dashboard);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <TopHeader
        title={message({ id: "eventSchemasImportReview" })}
        clearLink={links.customEventsSchemaList}
      />
      <div className={classes.wrapper}>
        <LoadingInterceptor isLoading={isLoading}>
          {areSuccessImports && (
            <SuccessfulImports
              customEventSchemas={customEventSchemasData.saved}
            />
          )}
          {areUnsuccessImports && (
            <UnsuccessfulImports
              customEventSchemas={customEventSchemasData.unsaved}
            />
          )}
        </LoadingInterceptor>
        <div className={classes.buttonWrapper}>
          <OLButton
            variant="contained"
            onClick={() => navigate(links.customEventsSchemaList)}
            data-test-id="view-event-schemas"
          >
            {message({ id: "viewEventSchemas" })}
          </OLButton>
        </div>
      </div>
    </>
  );
};
